import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { Award, Shield, CheckCircle2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const Certifications = () => {
  const [selectedCert, setSelectedCert] = useState(null);
  const [activeCertIndex, setActiveCertIndex] = useState(0);

  const certificates = [
    {
      id: '1',
      name: 'ISO 45001:2018',
      shortName: 'ISO',
      issuer: 'EQA IMS',
      description: 'Occupational Health & Safety Management System',
      scope: [
        'General Construction Renovation Works',
        'Electrical Engineering Works',
        'Metal Works Fabrication'
      ],
      certNo: 'OSH-17-1530',
      validUntil: '24 Feb 2027',
      image: 'https://customer-assets.emergentagent.com/job_yusenspaces/artifacts/ee1m0x85_image.png',
      icon: Shield,
      color: 'bg-blue-600'
    },
    {
      id: '2',
      name: 'bizSAFE Star',
      shortName: 'bizSAFE',
      issuer: 'WSH Council',
      description: 'Singapore Workplace Safety Certification',
      certNo: 'Star Level',
      validUntil: '24 Feb 2027',
      image: 'https://customer-assets.emergentagent.com/job_yusenspaces/artifacts/jiy0xr5y_image.png',
      icon: Award,
      color: 'bg-emerald-600'
    },
    {
      id: '3',
      name: 'BCA Licensed Builder',
      shortName: 'BCA GB2',
      issuer: 'BCA Singapore',
      description: 'General Builder Class 2 License',
      scope: [
        'Approved Person: KWAN YIN LENG',
        'Tech Controller: ANDUAN FRANCIS BIBIT'
      ],
      licenseCode: 'GB2',
      validUntil: '12 Aug 2028',
      image: 'https://customer-assets.emergentagent.com/job_yusenspaces/artifacts/g1po8ssq_image.png',
      icon: CheckCircle2,
      color: 'bg-orange-600'
    },
    {
      id: '4',
      name: 'BCA Registered Contractor',
      shortName: 'BCA CR',
      issuer: 'BCA Singapore',
      description: 'Registered for Multiple Workheads',
      workheads: [
        'CR06 - Interior Decoration (L2)',
        'CR11 - Signcraft Installation (L1)',
        'ME05 - Electrical Engineering (L1)'
      ],
      image: 'https://customer-assets.emergentagent.com/job_yusenspaces/artifacts/pdntn1op_image.png',
      icon: CheckCircle2,
      color: 'bg-violet-600'
    }
  ];

  const activeCert = certificates[activeCertIndex];
  const IconComponent = activeCert.icon;

  const nextCert = () => {
    setActiveCertIndex((prev) => (prev === certificates.length - 1 ? 0 : prev + 1));
  };

  const prevCert = () => {
    setActiveCertIndex((prev) => (prev === 0 ? certificates.length - 1 : prev - 1));
  };

  return (
    <section id="certifications" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header - Compact on mobile */}
        <div className="text-center mb-6 sm:mb-8">
          <Badge className="mb-2 sm:mb-4 bg-blue-600 text-white px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm hover:bg-blue-600">
            Certified & Licensed
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Certifications & Accreditations
          </h2>
          <p className="text-sm sm:text-base text-gray-600 hidden sm:block">
            Recognized by leading authorities in construction, safety, and quality
          </p>
        </div>

        {/* Horizontal Scrollable Certificate Tabs */}
        <div className="flex gap-2 mb-4 sm:mb-6 overflow-x-auto pb-2 scrollbar-hide">
          {certificates.map((cert, index) => {
            const CertIcon = cert.icon;
            const isActive = index === activeCertIndex;
            
            return (
              <button
                key={cert.id}
                onClick={() => setActiveCertIndex(index)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                  isActive 
                    ? `${cert.color} text-white shadow-lg` 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <CertIcon className="w-4 h-4" />
                <span className="text-xs sm:text-sm font-medium">{cert.shortName}</span>
              </button>
            );
          })}
        </div>

        {/* Content Card - Compact for mobile */}
        <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg">
          {/* Certificate Header */}
          <div className={`${activeCert.color} p-4 sm:p-6`}>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">
                  {activeCert.name}
                </h3>
                <p className="text-white/80 text-xs sm:text-sm">{activeCert.issuer}</p>
              </div>
            </div>
          </div>

          {/* Certificate Image with Navigation */}
          <div className="relative bg-white">
            <div className="aspect-[4/3] sm:aspect-[16/10] flex items-center justify-center p-4">
              <img
                src={activeCert.image}
                alt={activeCert.name}
                className="max-w-full max-h-full object-contain cursor-pointer hover:scale-105 transition-transform"
                style={{ mixBlendMode: 'multiply' }}
                onClick={() => setSelectedCert(activeCert)}
              />
            </div>
            
            {/* Navigation Arrows */}
            <button
              onClick={prevCert}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
            >
              <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
            </button>
            <button
              onClick={nextCert}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
            >
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
            </button>

            {/* Image Counter */}
            <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
              {activeCertIndex + 1} / {certificates.length}
            </div>

            {/* Click to enlarge hint */}
            <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
              Tap to enlarge
            </div>
          </div>

          {/* Content Section - Compact */}
          <div className="p-4 sm:p-6">
            {/* Description */}
            <p className="text-sm sm:text-base text-gray-600 mb-3 leading-relaxed">
              {activeCert.description}
            </p>

            {/* Scope/Workheads - Horizontal scroll on mobile */}
            {(activeCert.scope || activeCert.workheads) && (
              <div className="mb-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                  {activeCert.workheads ? 'Registered Workheads' : 'Certification Scope'}
                </h4>
                <div className="flex gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:overflow-visible">
                  {(activeCert.scope || activeCert.workheads).map((item) => (
                    <div 
                      key={item} 
                      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 rounded-full whitespace-nowrap flex-shrink-0"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 text-green-600" />
                      <span className="text-xs sm:text-sm text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Certificate Details - Compact grid */}
            <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-100">
              {activeCert.certNo && (
                <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                  <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Cert No.</p>
                  <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">{activeCert.certNo}</p>
                </div>
              )}
              {activeCert.licenseCode && (
                <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                  <p className="text-[10px] sm:text-xs text-gray-500 uppercase">License</p>
                  <p className="text-xs sm:text-sm font-semibold text-gray-900">{activeCert.licenseCode}</p>
                </div>
              )}
              {activeCert.validUntil && (
                <div className="bg-green-50 rounded-lg p-2 sm:p-3 col-span-2 sm:col-span-1">
                  <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Valid Until</p>
                  <p className="text-xs sm:text-sm font-semibold text-green-600">{activeCert.validUntil}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Stats Banner - Compact */}
        <div className="mt-6 sm:mt-8 grid grid-cols-4 gap-2 sm:gap-4">
          <div className="text-center p-2 sm:p-4 bg-white rounded-lg sm:rounded-xl shadow">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600 mb-0.5">ISO</div>
            <div className="text-[10px] sm:text-xs text-gray-500">Certified</div>
          </div>
          <div className="text-center p-2 sm:p-4 bg-white rounded-lg sm:rounded-xl shadow">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-emerald-600 mb-0.5">bizSAFE</div>
            <div className="text-[10px] sm:text-xs text-gray-500">Star</div>
          </div>
          <div className="text-center p-2 sm:p-4 bg-white rounded-lg sm:rounded-xl shadow">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-orange-600 mb-0.5">BCA</div>
            <div className="text-[10px] sm:text-xs text-gray-500">Licensed</div>
          </div>
          <div className="text-center p-2 sm:p-4 bg-white rounded-lg sm:rounded-xl shadow">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-violet-600 mb-0.5">38+</div>
            <div className="text-[10px] sm:text-xs text-gray-500">Years</div>
          </div>
        </div>
      </div>

      {/* Modal for Full Certificate View */}
      {selectedCert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setSelectedCert(null)}>
          <div className="relative max-w-4xl w-full bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <Button
              onClick={() => setSelectedCert(null)}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 bg-gray-900 hover:bg-gray-800 text-white rounded-full w-8 h-8 sm:w-10 sm:h-10 p-0"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </Button>
            <div className="p-4 sm:p-8">
              <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-4">{selectedCert.name}</h3>
              <div className="aspect-[4/3] rounded-xl overflow-hidden border-2 border-gray-200">
                <img
                  src={selectedCert.image}
                  alt={selectedCert.name}
                  className="w-full h-full object-contain bg-white"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Certifications;
